import {
  DrawerMovimientosMaquina,
  type DrawerMovimientosSel,
} from '@/components/dominio/DrawerMovimientosMaquina';
import {
  GraficoBarrasIngresoGasto,
  formatearCompacto,
} from '@/components/dominio/GraficoBarrasIngresoGasto';
import { FiltroPeriodos } from '@/components/filtros/FiltroPeriodos';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePeriodosDisponibles } from '@/hooks/useAreaCosteo';
import { useMaquinariaAnalisisGeneral } from '@/hooks/useMaquinariaAnalisisGeneral';
import type { PeriodoMaqSel } from '@/lib/cliente';
import { formatearCRC } from '@adelante/shared';
import type { ResumenGeneralMaquina } from '@adelante/shared';
import { CaretDown, CaretUp, CircleNotch, X } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

const PERIODO_MAQ_VACIO: PeriodoMaqSel = { aniosSel: [], mesesSel: [], diasSel: [] };

const NOMBRE_MES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

/** Normaliza para búsqueda: minúsculas + sin acentos. */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

type Columna = keyof ResumenGeneralMaquina | 'margen';
type Direccion = 'asc' | 'desc';

interface Fila extends ResumenGeneralMaquina {
  margen: number;
}

/** Tarjeta KPI simple: etiqueta + número grande. */
function TarjetaKpi({
  etiqueta,
  valor,
  tono = 'default',
}: {
  etiqueta: string;
  valor: string;
  tono?: 'default' | 'positivo' | 'negativo';
}) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs text-muted-foreground">{etiqueta}</p>
        <p
          className={`text-xl font-semibold tabular-nums ${
            tono === 'positivo' ? 'text-green-600' : tono === 'negativo' ? 'text-red-600' : ''
          }`}
        >
          {valor}
        </p>
      </CardContent>
    </Card>
  );
}

/** Línea de tendencia mensual (SVG a mano, sin librería) — Ingreso vs Gasto. */
function GraficoTendenciaMensual({
  puntos,
}: {
  puntos: { label: string; ingreso: number; gasto: number }[];
}) {
  const w = 720;
  const h = 220;
  const padL = 56;
  const padB = 26;
  const padT = 10;
  const padR = 12;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const maxY = Math.max(1, ...puntos.flatMap((p) => [p.ingreso, p.gasto]));
  const x = (i: number) =>
    padL + (puntos.length <= 1 ? innerW / 2 : (i / (puntos.length - 1)) * innerW);
  const y = (v: number) => padT + innerH - (v / maxY) * innerH;
  const pathFor = (clave: 'ingreso' | 'gasto') =>
    puntos.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p[clave])}`).join(' ');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => maxY * f);

  if (puntos.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-8 text-center">Sin datos mensuales aún.</p>
    );
  }

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" role="img">
      <title>Tendencia mensual de ingreso y gasto de maquinaria</title>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={padL} x2={w - padR} y1={y(t)} y2={y(t)} stroke="#e4e4e7" strokeWidth={1} />
          <text x={padL - 8} y={y(t) + 3} textAnchor="end" fontSize={9} fill="#71717a">
            {formatearCompacto(t)}
          </text>
        </g>
      ))}
      <path d={pathFor('ingreso')} fill="none" stroke="#16a34a" strokeWidth={2} />
      <path d={pathFor('gasto')} fill="none" stroke="#dc2626" strokeWidth={2} />
      {puntos.map((p, i) => (
        <g key={p.label}>
          <circle cx={x(i)} cy={y(p.ingreso)} r={3} fill="#16a34a" />
          <circle cx={x(i)} cy={y(p.gasto)} r={3} fill="#dc2626" />
          <text x={x(i)} y={h - 6} textAnchor="middle" fontSize={9} fill="#71717a">
            {p.label}
          </text>
        </g>
      ))}
    </svg>
  );
}

/** Barras horizontales de una sola serie (gasto por categoría), con % del total. */
function DesgloseCategorias({ datos }: { datos: { categoria: string; totalGastoLcy: number }[] }) {
  const total = datos.reduce((s, d) => s + d.totalGastoLcy, 0) || 1;
  const max = Math.max(1, ...datos.map((d) => d.totalGastoLcy));
  if (datos.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-8 text-center">Sin datos de gasto aún.</p>
    );
  }
  return (
    <div className="space-y-2.5">
      {datos.map((d) => (
        <div key={d.categoria}>
          <div className="flex justify-between text-xs mb-0.5">
            <span className="font-medium">{d.categoria}</span>
            <span className="tabular-nums text-muted-foreground">
              {formatearCRC(d.totalGastoLcy)} ({((d.totalGastoLcy / total) * 100).toFixed(1)}%)
            </span>
          </div>
          <div className="h-2.5 rounded bg-zinc-100 overflow-hidden">
            <div
              className="h-2.5 rounded bg-amber-500"
              style={{ width: `${Math.max(1, (d.totalGastoLcy / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Análisis General de Maquinaria: dashboard con tarjetas KPI, gráfico de
 * ingreso/gasto por máquina, tendencia mensual, desglose del gasto por
 * categoría y tabla completa ordenable con drill-down por movimiento (click
 * en Ingreso Partes / Ingreso Recursos / Gasto). Filtro de período propio de
 * Maquinaria, independiente del selector global.
 */
export function MaquinariaGeneralPantalla() {
  const [periodoMaq, setPeriodoMaq] = useState<PeriodoMaqSel>(PERIODO_MAQ_VACIO);
  const { data: fechasData } = usePeriodosDisponibles();
  const { data, isLoading, error } = useMaquinariaAnalisisGeneral(periodoMaq);
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState<{ columna: Columna; direccion: Direccion }>({
    columna: 'totalIngresoLcy',
    direccion: 'desc',
  });
  const [drawerSel, setDrawerSel] = useState<DrawerMovimientosSel | null>(null);

  const busquedaNorm = normalizar(busqueda);

  const filas = useMemo<Fila[]>(() => {
    return (data?.data?.porMaquina ?? []).map((r) => ({
      ...r,
      margen: r.totalIngresoLcy - r.totalGastoLcy,
    }));
  }, [data]);

  const filasOrdenadas = useMemo(() => {
    let resultado = filas;
    if (busquedaNorm) {
      resultado = resultado.filter(
        (f) =>
          normalizar(f.codigoMaquina).includes(busquedaNorm) ||
          normalizar(f.nombreMaquina ?? '').includes(busquedaNorm),
      );
    }
    const { columna, direccion } = orden;
    const signo = direccion === 'asc' ? 1 : -1;
    return [...resultado].sort((a, b) => {
      const va = a[columna];
      const vb = b[columna];
      if (typeof va === 'string' || typeof vb === 'string') {
        return signo * String(va ?? '').localeCompare(String(vb ?? ''), 'es', { numeric: true });
      }
      return signo * ((va as number) - (vb as number));
    });
  }, [filas, busquedaNorm, orden]);

  const totales = useMemo(
    () =>
      filas.reduce(
        (acc, f) => ({
          horas: acc.horas + f.totalHoras,
          ingresoPartes: acc.ingresoPartes + f.totalIngresoPartesLcy,
          ingresoRecursos: acc.ingresoRecursos + f.totalIngresoRecursosLcy,
          ingreso: acc.ingreso + f.totalIngresoLcy,
          gasto: acc.gasto + f.totalGastoLcy,
          margen: acc.margen + f.margen,
        }),
        { horas: 0, ingresoPartes: 0, ingresoRecursos: 0, ingreso: 0, gasto: 0, margen: 0 },
      ),
    [filas],
  );

  const top15 = useMemo(
    () =>
      [...filas]
        .sort((a, b) => b.totalIngresoLcy + b.totalGastoLcy - (a.totalIngresoLcy + a.totalGastoLcy))
        .slice(0, 15)
        .map((f) => ({
          etiqueta: f.nombreMaquina ? `${f.codigoMaquina} — ${f.nombreMaquina}` : f.codigoMaquina,
          ingreso: f.totalIngresoLcy,
          gasto: f.totalGastoLcy,
        })),
    [filas],
  );

  const puntosMensuales = useMemo(
    () =>
      (data?.data?.porMes ?? []).map((p) => ({
        label: `${NOMBRE_MES[p.mes - 1] ?? p.mes} ${String(p.anio).slice(2)}`,
        ingreso: p.totalIngresoLcy,
        gasto: p.totalGastoLcy,
      })),
    [data],
  );

  const ordenarPor = (columna: Columna) => {
    setOrden((p) =>
      p.columna === columna
        ? { columna, direccion: p.direccion === 'asc' ? 'desc' : 'asc' }
        : { columna, direccion: columna === 'codigoMaquina' ? 'asc' : 'desc' },
    );
  };

  const IconoOrden = ({ columna }: { columna: Columna }) => {
    if (orden.columna !== columna) return null;
    return orden.direccion === 'asc' ? (
      <CaretUp className="h-3 w-3 inline ml-1" weight="bold" />
    ) : (
      <CaretDown className="h-3 w-3 inline ml-1" weight="bold" />
    );
  };

  const encabezado = (
    columna: Columna,
    etiqueta: string,
    alineacion: 'left' | 'right' = 'left',
  ) => (
    <TableHead
      className={`text-xs cursor-pointer select-none hover:bg-zinc-100 ${alineacion === 'right' ? 'text-right' : ''}`}
      onClick={() => ordenarPor(columna)}
    >
      {etiqueta}
      <IconoOrden columna={columna} />
    </TableHead>
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
        Cargando…
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Análisis General de Maquinaria</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Ingreso (partes de trabajo + recursos vía ensamblados) vs. gasto real (combustible, mano
          de obra, mantenimiento — proyecto MAQ) por máquina. Click en un monto de Ingreso Partes,
          Ingreso Recursos o Gasto para ver el detalle de movimientos.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Período (solo Maquinaria):</span>
        <FiltroPeriodos
          fechasDisponibles={fechasData?.data ?? []}
          aniosSel={periodoMaq.aniosSel}
          mesesSel={periodoMaq.mesesSel}
          diasSel={periodoMaq.diasSel}
          onChange={setPeriodoMaq}
        />
        <span className="text-xs text-muted-foreground">
          — sin selección, muestra el acumulado histórico completo.
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <TarjetaKpi
          etiqueta="Ingreso Total"
          valor={formatearCRC(totales.ingreso)}
          tono="positivo"
        />
        <TarjetaKpi etiqueta="Gasto Total" valor={formatearCRC(totales.gasto)} />
        <TarjetaKpi
          etiqueta="Margen Total"
          valor={formatearCRC(totales.margen)}
          tono={totales.margen < 0 ? 'negativo' : 'positivo'}
        />
        <TarjetaKpi etiqueta="Máquinas" valor={filas.length.toLocaleString('es-CR')} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Top 15 máquinas — Ingreso vs. Gasto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <GraficoBarrasIngresoGasto datos={top15} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Tendencia mensual</CardTitle>
          </CardHeader>
          <CardContent>
            <GraficoTendenciaMensual puntos={puntosMensuales} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Gasto por categoría</CardTitle>
        </CardHeader>
        <CardContent>
          <DesgloseCategorias datos={data?.data?.porCategoriaGasto ?? []} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium">
            Máquinas ({filasOrdenadas.length.toLocaleString('es-CR')})
          </CardTitle>
          <div className="relative">
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar máquina…"
              className="h-7 w-56 rounded border border-zinc-300 pl-2 pr-6 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-zinc-100 hover:text-foreground"
                title="Limpiar búsqueda"
                aria-label="Limpiar búsqueda"
              >
                <X className="h-3 w-3" weight="bold" />
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {encabezado('codigoMaquina', 'Máquina')}
                {encabezado('totalHoras', 'Horas', 'right')}
                {encabezado('totalIngresoPartesLcy', 'Ingreso Partes', 'right')}
                {encabezado('totalIngresoRecursosLcy', 'Ingreso Recursos', 'right')}
                {encabezado('totalIngresoLcy', 'Ingreso Total', 'right')}
                {encabezado('totalGastoLcy', 'Gasto', 'right')}
                {encabezado('margen', 'Margen', 'right')}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filasOrdenadas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-6">
                    {busqueda ? `Sin resultados para "${busqueda}".` : 'Sin registros.'}
                  </TableCell>
                </TableRow>
              ) : (
                filasOrdenadas.map((f) => (
                  <TableRow key={f.codigoMaquina}>
                    <TableCell className="text-xs">
                      {f.codigoMaquina}
                      {f.nombreMaquina && (
                        <span className="text-muted-foreground"> — {f.nombreMaquina}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      {f.totalHoras.toLocaleString('es-CR')}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      <button
                        type="button"
                        className="hover:underline decoration-dotted underline-offset-2 disabled:no-underline disabled:cursor-default disabled:opacity-50"
                        disabled={f.totalIngresoPartesLcy === 0}
                        onClick={() =>
                          setDrawerSel({
                            maquina: f.codigoMaquina,
                            nombreMaquina: f.nombreMaquina,
                            modo: 'ingresoPartes',
                          })
                        }
                        title="Ver movimientos de Partes de Trabajo"
                      >
                        {formatearCRC(f.totalIngresoPartesLcy)}
                      </button>
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      <button
                        type="button"
                        className="hover:underline decoration-dotted underline-offset-2 disabled:no-underline disabled:cursor-default disabled:opacity-50"
                        disabled={f.totalIngresoRecursosLcy === 0}
                        onClick={() =>
                          setDrawerSel({
                            maquina: f.codigoMaquina,
                            nombreMaquina: f.nombreMaquina,
                            modo: 'ingresoRecursos',
                          })
                        }
                        title="Ver movimientos de Recursos (Ensamblados)"
                      >
                        {formatearCRC(f.totalIngresoRecursosLcy)}
                      </button>
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums font-medium">
                      {formatearCRC(f.totalIngresoLcy)}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      <button
                        type="button"
                        className="hover:underline decoration-dotted underline-offset-2 disabled:no-underline disabled:cursor-default disabled:opacity-50"
                        disabled={f.totalGastoLcy === 0}
                        onClick={() =>
                          setDrawerSel({
                            maquina: f.codigoMaquina,
                            nombreMaquina: f.nombreMaquina,
                            modo: 'gasto',
                          })
                        }
                        title="Ver movimientos de gasto"
                      >
                        {formatearCRC(f.totalGastoLcy)}
                      </button>
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      <span className={f.margen < 0 ? 'text-red-600' : 'text-green-600'}>
                        {formatearCRC(f.margen)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            {filasOrdenadas.length > 0 && (
              <tfoot>
                <TableRow className="bg-zinc-50 font-medium">
                  <TableCell className="text-xs">Total</TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {totales.horas.toLocaleString('es-CR')}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {formatearCRC(totales.ingresoPartes)}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {formatearCRC(totales.ingresoRecursos)}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {formatearCRC(totales.ingreso)}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {formatearCRC(totales.gasto)}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    <span className={totales.margen < 0 ? 'text-red-600' : 'text-green-600'}>
                      {formatearCRC(totales.margen)}
                    </span>
                  </TableCell>
                </TableRow>
              </tfoot>
            )}
          </Table>
        </CardContent>
      </Card>

      <DrawerMovimientosMaquina
        sel={drawerSel}
        periodoMaq={periodoMaq}
        onClose={() => setDrawerSel(null)}
      />
    </div>
  );
}
